import {
  Link,
  rootRouteId,
  useMatch,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { Button } from "~/components/ui/button";
import { captureAppException } from "~/lib/sentry";
import { m } from "~/lib/i18n";

/* The error rendered as a trading halt: a choppy-but-healthy trend line that
   stops at a dashed halt marker with a still-pulsing tick — paused, not dead —
   and a dashed continuation ahead of it. Paths carry pathLength={1} so the
   CSS draw-in in app.css stays geometry-independent. */
function HaltChart() {
  return (
    <svg
      aria-hidden="true"
      className="w-full"
      fill="none"
      viewBox="0 0 384 120"
    >
      <g className="text-border" stroke="currentColor" strokeWidth="1">
        <line x1="0" x2="384" y1="24" y2="24" />
        <line x1="0" x2="384" y1="64" y2="64" />
        <line strokeDasharray="3 5" x1="0" x2="384" y1="104" y2="104" />
      </g>
      <path
        className="error-page-trend text-muted-foreground"
        d="M0 62 L16 50 L32 66 L48 46 L64 58 L78 44 L96 60 L110 50 L128 68 L142 54 L158 64 L174 48 L190 60 L204 52 L220 58"
        pathLength={1}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <line
        className="error-page-halt-marker text-status-warning-foreground"
        opacity="0.45"
        stroke="currentColor"
        strokeDasharray="4 5"
        strokeWidth="1.5"
        x1="220"
        x2="220"
        y1="16"
        y2="108"
      />
      <line
        className="error-page-continuation text-border"
        stroke="currentColor"
        strokeDasharray="3 6"
        strokeWidth="1.5"
        x1="232"
        x2="384"
        y1="58"
        y2="58"
      />
      <circle
        className="error-page-halo text-status-warning-foreground"
        cx="220"
        cy="58"
        fill="currentColor"
        r="5"
      />
      <g className="error-page-dot text-status-warning-foreground">
        <circle
          cx="220"
          cy="58"
          opacity="0.25"
          r="7"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <circle cx="220" cy="58" fill="currentColor" r="3" />
      </g>
    </svg>
  );
}

export function DefaultCatchBoundary({ error }: ErrorComponentProps) {
  const router = useRouter();
  const lastCapturedErrorRef = useRef<unknown>(null);
  const isRoot = useMatch({
    strict: false,
    select: (state) => state.id === rootRouteId,
  });

  useEffect(() => {
    if (lastCapturedErrorRef.current === error) {
      return;
    }

    lastCapturedErrorRef.current = error;
    captureAppException(error, {
      extra: {
        routeId: isRoot ? rootRouteId : undefined,
      },
      name: "tanstack_route_error_boundary",
    });
  }, [error, isRoot]);

  const errorMessage = error instanceof Error ? error.message : String(error);

  return (
    <div className="flex min-h-[calc(100dvh-4rem)] w-full flex-1 items-center justify-center px-6 py-16">
      <div className="flex w-full max-w-sm flex-col items-center text-center">
        <HaltChart />
        <div
          aria-hidden="true"
          className="error-page-rise mt-7 flex items-baseline gap-3 [animation-delay:60ms]"
        >
          <span className="text-[2.5rem] leading-none font-medium tracking-tight tabular-nums text-muted-foreground">
            $–.––
          </span>
          <span className="text-caption rounded-full bg-warning-accent-1000/15 px-2.5 py-1 font-medium text-status-warning-foreground">
            {m.components_default_catch_boundary_halted_label()}
          </span>
        </div>
        <h1 className="error-page-rise text-title-sm mt-5 font-medium tracking-tight [animation-delay:120ms]">
          {m.components_default_catch_boundary_title()}
        </h1>
        <p className="error-page-rise text-meta mt-1.5 text-balance text-muted-foreground [animation-delay:180ms]">
          {m.components_default_catch_boundary_description()}
        </p>
        <div className="error-page-rise mt-7 flex flex-wrap justify-center gap-2 [animation-delay:240ms]">
          {isRoot ? (
            <Link className="inline-flex" to="/">
              <Button render={<span />} variant="outline">
                {m.components_default_catch_boundary_home_label()}
              </Button>
            </Link>
          ) : (
            <Button
              onClick={() => window.history.back()}
              type="button"
              variant="outline"
            >
              {m.components_default_catch_boundary_back_label()}
            </Button>
          )}
          <Button onClick={() => router.invalidate()} type="button">
            {m.components_default_catch_boundary_retry_label()}
          </Button>
        </div>
        <details className="error-page-rise mt-6 w-full [animation-delay:300ms]">
          <summary className="text-caption cursor-pointer text-muted-foreground/70 transition-colors hover:text-muted-foreground">
            {m.components_default_catch_boundary_details_label()}
          </summary>
          <p className="mt-2 font-mono text-caption break-all text-muted-foreground">
            {errorMessage}
          </p>
        </details>
      </div>
    </div>
  );
}
